import { toast } from 'sonner'
import i18n from '@/i18n'
import { slugifyKey } from './templateHelpers'
import type { DocBlock, DocField, FieldColumns, SablonSurum } from '../types'
import { createTable } from './tableOps'

export function addPage(surum: SablonSurum): SablonSurum {
  const id = `pg-${crypto.randomUUID().slice(0, 8)}`
  const secId = `sec-${crypto.randomUUID().slice(0, 8)}`
  const order = surum.pages.length
  return {
    ...surum,
    pages: [...surum.pages, { id, title: `${order + 1}. Sayfa`, order }],
    sections: [...surum.sections, { id: secId, title: 'Yeni Bölüm', pageId: id, order: 0 }],
    blocksBySection: {
      ...surum.blocksBySection,
      [secId]: [{ id: `b-${crypto.randomUUID().slice(0, 8)}`, kind: 'heading', text: 'Yeni Bölüm', level: 2 }],
    },
  }
}

export function addSection(surum: SablonSurum, pageId: string): SablonSurum {
  const id = `sec-${crypto.randomUUID().slice(0, 8)}`
  const pageSections = surum.sections.filter((s) => s.pageId === pageId)
  return {
    ...surum,
    sections: [
      ...surum.sections,
      { id, title: i18n.t('hr.belgeler.structure.newSection'), pageId, order: pageSections.length },
    ],
    blocksBySection: {
      ...surum.blocksBySection,
      [id]: [],
    },
  }
}

export function moveSection(surum: SablonSurum, sectionId: string, dir: -1 | 1): SablonSurum {
  const section = surum.sections.find((s) => s.id === sectionId)
  if (!section) return surum
  const siblings = surum.sections.filter((s) => s.pageId === section.pageId).sort((a, b) => a.order - b.order)
  const idx = siblings.findIndex((s) => s.id === sectionId)
  const swap = siblings[idx + dir]
  if (!swap) return surum
  return {
    ...surum,
    sections: surum.sections.map((s) => {
      if (s.id === section.id) return { ...s, order: swap.order }
      if (s.id === swap.id) return { ...s, order: section.order }
      return s
    }),
  }
}

export const SECTION_DROP_PREFIX = 'section:'

export function moveBlock(surum: SablonSurum, activeId: string, overId: string): SablonSurum {
  if (activeId === overId) return surum
  const entries = Object.entries(surum.blocksBySection)
  const from = entries.find(([, blocks]) => blocks.some((b) => b.id === activeId))
  if (!from) return surum
  const [fromSec, fromBlocks] = from
  const toSec = overId.startsWith(SECTION_DROP_PREFIX)
    ? overId.slice(SECTION_DROP_PREFIX.length)
    : entries.find(([, blocks]) => blocks.some((b) => b.id === overId))?.[0]
  if (!toSec) return surum

  const moving = fromBlocks.find((b) => b.id === activeId)
  if (!moving) return surum
  const source = fromBlocks.filter((b) => b.id !== activeId)
  const target = fromSec === toSec ? source : [...(surum.blocksBySection[toSec] ?? [])]
  const overIndex = target.findIndex((b) => b.id === overId)
  const originalIndex = fromBlocks.findIndex((b) => b.id === activeId)
  const sectionOrder = (id: string) => surum.sections.find((s) => s.id === id)?.order ?? 0
  const movingDown = fromSec === toSec ? originalIndex <= overIndex : sectionOrder(fromSec) < sectionOrder(toSec)
  const insertAt = overIndex < 0 ? target.length : movingDown ? overIndex + 1 : overIndex
  target.splice(insertAt, 0, moving)

  const movedFieldId = moving.kind === 'field' || moving.kind === 'signature' ? moving.fieldId : null
  const pageId = surum.sections.find((s) => s.id === toSec)?.pageId
  return {
    ...surum,
    fields:
      movedFieldId && fromSec !== toSec
        ? surum.fields.map((f) => (f.id === movedFieldId ? { ...f, sectionId: toSec, pageId: pageId ?? f.pageId } : f))
        : surum.fields,
    blocksBySection: { ...surum.blocksBySection, [fromSec]: source, [toSec]: target },
  }
}

export function findBlockIdByField(surum: SablonSurum, fieldId: string): string | undefined {
  for (const blocks of Object.values(surum.blocksBySection)) {
    const hit = blocks.find((b) => (b.kind === 'field' || b.kind === 'signature') && b.fieldId === fieldId)
    if (hit) return hit.id
  }
  return undefined
}

export function blockColumns(block: DocBlock): FieldColumns {
  return block.kind === 'field' || block.kind === 'signature' ? block.columns ?? 1 : 1
}

export function getFieldColumns(surum: SablonSurum, fieldId: string): FieldColumns {
  for (const blocks of Object.values(surum.blocksBySection)) {
    const hit = blocks.find((b) => (b.kind === 'field' || b.kind === 'signature') && b.fieldId === fieldId)
    if (hit) return blockColumns(hit)
  }
  return 1
}

export function setFieldColumns(surum: SablonSurum, fieldId: string, columns: FieldColumns): SablonSurum {
  const blocksBySection: SablonSurum['blocksBySection'] = {}
  for (const [sectionId, blocks] of Object.entries(surum.blocksBySection)) {
    blocksBySection[sectionId] = blocks.map((b) =>
      (b.kind === 'field' || b.kind === 'signature') && b.fieldId === fieldId ? { ...b, columns } : b,
    )
  }
  return { ...surum, blocksBySection }
}

export function removeBlock(surum: SablonSurum, blockId: string): SablonSurum {
  const entry = Object.entries(surum.blocksBySection).find(([, blocks]) => blocks.some((b) => b.id === blockId))
  if (!entry) return surum
  const [sectionId, blocks] = entry
  const block = blocks.find((b) => b.id === blockId)
  const fieldId = block && (block.kind === 'field' || block.kind === 'signature') ? block.fieldId : null
  return {
    ...surum,
    fields: fieldId ? surum.fields.filter((f) => f.id !== fieldId) : surum.fields,
    blocksBySection: { ...surum.blocksBySection, [sectionId]: blocks.filter((b) => b.id !== blockId) },
  }
}

export function addContentBlock(
  surum: SablonSurum,
  sectionId: string,
  kind: 'text' | 'table' | 'image' | 'divider',
): SablonSurum {
  const blocks = [...(surum.blocksBySection[sectionId] ?? [])]
  if (kind === 'text') {
    blocks.push({
      id: `b-${crypto.randomUUID().slice(0, 8)}`,
      kind: 'richtext',
      html: '<p>Yeni metin bloğu</p>',
    })
  } else if (kind === 'divider') {
    blocks.push({ id: `b-${crypto.randomUUID().slice(0, 8)}`, kind: 'divider' })
  } else if (kind === 'table') {
    blocks.push({
      id: `b-${crypto.randomUUID().slice(0, 8)}`,
      kind: 'table',
      rows: createTable([1, 2, 3].map((n) => i18n.t('hr.belgeler.table.column', { n }))),
    })
  } else {
    toast.info(i18n.t('hr.belgeler.structure.imageUnavailable'))
    return surum
  }
  return { ...surum, blocksBySection: { ...surum.blocksBySection, [sectionId]: blocks } }
}

export function createFieldOnSection(
  surum: SablonSurum,
  sectionId: string,
  pageId: string,
  partial: Pick<DocField, 'label' | 'type' | 'source' | 'variable' | 'key'>,
  columns: FieldColumns = 1,
): { surum: SablonSurum; fieldId: string } {
  const id = `f-${crypto.randomUUID().slice(0, 8)}`
  const field: DocField = {
    id,
    key: partial.key || slugifyKey(partial.label),
    label: partial.label,
    type: partial.type,
    source: partial.source,
    variable: partial.variable,
    required: partial.source === 'personel' && partial.type !== 'checkbox',
    sectionId,
    pageId,
    placeholder: partial.type === 'telefon' ? '(___) ___ __ __' : undefined,
  }
  const block: DocBlock =
    partial.type === 'imza'
      ? { id: `b-${crypto.randomUUID().slice(0, 8)}`, kind: 'signature', fieldId: id, columns }
      : { id: `b-${crypto.randomUUID().slice(0, 8)}`, kind: 'field', fieldId: id, columns }
  const blocks = [...(surum.blocksBySection[sectionId] ?? []), block]
  return {
    fieldId: id,
    surum: {
      ...surum,
      fields: [...surum.fields, field],
      blocksBySection: { ...surum.blocksBySection, [sectionId]: blocks },
    },
  }
}

export function insertVariableAsField(
  surum: SablonSurum,
  sectionId: string,
  pageId: string,
  variableKey: string,
  label: string,
): { surum: SablonSurum; fieldId: string } {
  return createFieldOnSection(surum, sectionId, pageId, {
    label,
    type: 'kisa_metin',
    source: 'otomatik',
    variable: variableKey,
    key: variableKey.split('.').pop() ?? slugifyKey(label),
  })
}
